import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SiteSettingsProvider } from '@/contexts/SiteSettingsContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Lessons from '@/pages/Lessons';
import Pricing from '@/pages/Pricing';
import Location from '@/pages/Location';
import Testimonials from '@/pages/Testimonials';
import FAQ from '@/pages/FAQ';
import Resources from '@/pages/Resources';
import Contact from '@/pages/Contact';
import PortalLogin from '@/pages/PortalLogin';
import StudentPortal from '@/pages/StudentPortal';
import AdminDashboard from '@/pages/AdminDashboard';
import '@/App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, role, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#F5F0F7] border-t-[#4B1D5E] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/portal-login" replace />;
  if (requiredRole && role !== requiredRole) {
    return <Navigate to={role === 'admin' ? '/admin' : role === 'student' ? '/portal' : '/portal-login'} replace />;
  }
  return children;
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-[60vh]">{children}</main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public pages */}
        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
        <Route path="/lessons" element={<PublicLayout><Lessons /></PublicLayout>} />
        <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
        <Route path="/location" element={<PublicLayout><Location /></PublicLayout>} />
        <Route path="/testimonials" element={<PublicLayout><Testimonials /></PublicLayout>} />
        <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
        <Route path="/resources" element={<PublicLayout><Resources /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
        <Route path="/portal-login" element={<PublicLayout><PortalLogin /></PublicLayout>} />

        {/* Protected routes */}
        <Route path="/portal" element={<ProtectedRoute requiredRole="student"><StudentPortal /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteSettingsProvider>
          <AppRoutes />
        </SiteSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
